import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Home, Inbox, MapPin } from "lucide-react";
import { DeliveryPicker, deliveryError, type DeliveryValue } from "~/components/delivery-picker";
import { ResponsiveSheet } from "~/components/responsive-sheet";
import { Button } from "~/components/ui/button";
import { api } from "~/lib/api";
import { useMe } from "~/lib/auth";
import { deliveryLabel } from "~/lib/parcel-status";
import type { IParcel } from "~/lib/proto";

const ICONS: Record<string, typeof Home> = { locker: Inbox, home: Home, custom: MapPin };
const CHANGEABLE = ["REGISTERED", "RECEIVED_ABROAD"];

function valueOf(parcel: IParcel): DeliveryValue {
  return {
    method: parcel.deliveryMethod || "",
    area: parcel.deliveryArea || "",
    address: parcel.deliveryMethod === "custom" ? parcel.deliveryAddress || "" : "",
  };
}

export function DeliverySummary({ parcel }: { parcel: IParcel }) {
  const qc = useQueryClient();
  const { data: me } = useMe();
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState<DeliveryValue>(() => valueOf(parcel));
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const Icon = ICONS[parcel.deliveryMethod ?? ""] ?? MapPin;
  const changeable = CHANGEABLE.includes(parcel.status ?? "");
  const settled = parcel.status === "DELIVERED";

  function startChange() {
    setValue(valueOf(parcel));
    setError(null);
    setOpen(true);
  }

  async function save() {
    const problem = deliveryError(value, me);
    if (problem) return setError(problem);
    setBusy(true);
    setError(null);
    try {
      await api.setDelivery(parcel.id!, {
        deliveryMethod: value.method,
        deliveryArea: value.method === "locker" ? value.area.trim() : undefined,
        deliveryAddress: value.method === "custom" ? value.address.trim() : undefined,
      });
      await qc.invalidateQueries({ queryKey: ["parcel", parcel.id] });
      void qc.invalidateQueries({ queryKey: ["parcels"] });
      setOpen(false);
    } catch {
      setError("We could not save that. Check your connection and try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="border-hairline bg-card rounded-xl border p-5">
      <div className="flex items-center gap-3">
        <div className="bg-muted grid size-10 shrink-0 place-items-center rounded-full">
          <Icon className="size-5" strokeWidth={1.75} />
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="text-muted-foreground text-[13px]">{settled ? "Delivered to" : "Delivery"}</h2>
          <p className="text-[15px] font-medium wrap-anywhere">{deliveryLabel(parcel)}</p>
        </div>
        {changeable && (
          <Button variant="outline" className="h-11 shrink-0 px-4 text-[14px]" onClick={startChange}>
            Change
          </Button>
        )}
      </div>
      {!changeable && !settled && (
        <p className="text-muted-foreground type-footnote mt-3">
          This is fixed now that your parcel is in Kenya. Message us below if it has to change.
        </p>
      )}

      <ResponsiveSheet open={open} onOpenChange={setOpen} title="Change delivery">
        <h2 className="type-headline pr-8">Where should we deliver it?</h2>
        <p className="text-muted-foreground type-footnote mt-1">You can change this until your parcel arrives in Kenya.</p>
        <div className="mt-4">
          <DeliveryPicker
            value={value}
            error={error}
            onChange={(next) => {
              setValue(next);
              setError(null);
            }}
          />
        </div>
        <Button size="touch" className="mt-5 w-full" loading={busy} onClick={() => void save()}>
          Save delivery
        </Button>
      </ResponsiveSheet>
    </section>
  );
}
